/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
/* eslint-disable react/prop-types */
/* eslint-disable import/no-extraneous-dependencies */
import React, {
  useState, useEffect, useContext, useCallback,
} from 'react';
import { Menu } from '@mui/material';
import { Filter } from 'lucide-react';
import { useLocation, useHistory } from 'react-router';
import { UserContext } from '../../../contexts/User';
import { sendRequest } from '../../../api/shootAPI';
import API from '../../../api/endpoints';
import '../settings.css';
import DataTable from '../../../components/DataTable';
import RowActionMenu from '../../../components/RowActionMenu';
import useDebounce from '../../../hooks/useDebounce';
import MultiSelect from '../../../components/muiCustomized/MultiSelect';
import EditModal from './EditModal';

const TEAM_CHIP = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap';

function TeamManagement() {
  const { search } = useLocation();
  const history = useHistory();
  const query = new URLSearchParams(search);
  const page = query.get('page');
  const { activeWorkspace } = useContext(UserContext);
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState({});
  const [pageSize, setPageSize] = useState(10);
  const [totalCounts, setTotalCounts] = useState(0);
  const [currentPage, setCurrentPage] = useState(page ? parseInt(page, 10) : 1);
  const [teams, setTeams] = useState([]);
  const [ptoPolicies, setPtoPolicies] = useState([]);
  const [sortKey, setSortKey] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const [filterMembers, setFilterMembers] = useState([]);
  const [filterDesignations, setFilterDesignations] = useState([]);
  const [filterTeams, setFilterTeams] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([{ id: 0, name: 'All' }]);
  const [selectedDesignations, setSelectedDesignations] = useState([{ id: 0, name: 'All' }]);
  const [selectedTeams, setSelectedTeams] = useState([{ id: 0, name: 'All' }]);
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([{ id: 0, name: 'All' }]);

  const [onlyUnassigned, setOnlyUnassigned] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const { debounce } = useDebounce();

  const getMembers = useCallback(async (params) => {
    if (activeWorkspace) {
      setIsLoading(true);
      const response = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.teamManagementMembers(activeWorkspace.id),
        params,
      });

      if (response.success) {
        if (params?.pageSize) {
          setMembers(response.data?.members ?? []);
          setTotalCounts(response.data?.pageInfo?.totalCounts ?? 0);
        } else {
          const allMembers = response.data?.members ?? [];
          const mappedMembers = allMembers.map((member) => ({
            id: member.id,
            name: member.user?.fullName,
          }));
          setFilterMembers([{ id: 0, name: 'All' }, ...mappedMembers]);

          const locationsArray = allMembers.map((member) => (member.user.userLocation.country));
          const uniqueLocations = [...new Set(locationsArray)];
          setLocations(uniqueLocations.map((location) => ({ id: location, name: location })));

          // Designations actually present on members in this workspace
          const uniqueDesignations = [...new Set(allMembers.map((member) => member.designation).filter(Boolean))];
          setFilterDesignations(uniqueDesignations.map((d) => ({ id: d, name: d })));

          // Teams actually assigned to members in this workspace
          const teamMap = new Map();
          allMembers.forEach((member) => (member.teamMates || []).forEach((tm) => {
            if (tm.team) teamMap.set(tm.team.id, tm.team.name);
          }));
          setFilterTeams([...teamMap].map(([id, name]) => ({ id, name })));
        }
        setIsLoading(false);
      }
    }
  }, [activeWorkspace]);

  const handleSort = debounce((pSortKey, pSortOrder) => {
    setSortKey(pSortKey);
    setSortOrder(pSortOrder);
  }, 250);

  const toggleSort = (key) => {
    const nextOrder = key === sortKey && sortOrder === 'asc' ? 'desc' : 'asc';
    handleSort(key, nextOrder);
  };

  const handleSearch = async () => {
    const memberIds = selectedMembers.map((item) => item.id);
    const designations = selectedDesignations.map((item) => item.id);
    const teamIds = selectedTeams.map((item) => item.id);
    const locationIds = selectedLocations.map((item) => item.id);

    await getMembers({
      page: currentPage,
      pageSize,
      memberIds: memberIds[0] === 0 ? null : memberIds,
      designations: designations[0] === 0 ? null : designations,
      teams: teamIds[0] === 0 ? null : teamIds,
      locations: locationIds[0] === 0 ? null : locationIds,
      onlyUnassigned,
      sortKey,
      sortOrder,
    });
  };

  const handlePageChange = (next) => {
    setCurrentPage(next);
  };

  const handlePageSizeChange = (newSize) => {
    setCurrentPage(1);
    setPageSize(newSize);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleReset = () => {
    setSelectedMembers([{ id: 0, name: 'All' }]);
    setSelectedDesignations([{ id: 0, name: 'All' }]);
    setSelectedTeams([{ id: 0, name: 'All' }]);
    setSelectedLocations([{ id: 0, name: 'All' }]);
    setCurrentPage(1);
    // Refetch immediately with cleared filters (don't wait for state to flush)
    getMembers({
      page: 1,
      pageSize,
      memberIds: null,
      designations: null,
      teams: null,
      locations: null,
      onlyUnassigned,
      sortKey,
      sortOrder,
    });
  };

  const handleUnassigned = async () => {
    setCurrentPage(1);
    if (onlyUnassigned) {
      await getMembers({
        page: 1,
        pageSize,
        onlyUnassigned: false,
      });
    } else {
      await getMembers({
        page: 1,
        pageSize,
        onlyUnassigned: true,
      });
    }
    setOnlyUnassigned(!onlyUnassigned);
  };

  useEffect(() => {
    getMembers({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, currentPage, sortKey, sortOrder]);

  const getTeams = async () => {
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.getAllTeamInfo(activeWorkspace.id),
      timeout: 60000,
    });

    if (response.success) {
      setTeams(response.data.teams);
    }
  };

  const getPtoPolicies = async () => {
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.offTimePolicies(activeWorkspace.id),
      timeout: 60000,
    });

    if (response.success) {
      setPtoPolicies(response.data.ptoPolicies.filter((policy) => policy.isArchived === false));
    }
  };

  useEffect(() => {
    getTeams();
    getPtoPolicies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      key: 'name',
      label: 'Employee Name',
      sortable: true,
      width: 200,
      flex: true,
      render: (m) => <span className="font-medium text-ink-900">{m.user?.fullName || '—'}</span>,
    },
    {
      key: 'designation',
      label: 'Designation',
      sortable: true,
      width: 150,
      render: (m) => <span className="text-ink-700">{m.designation || '—'}</span>,
    },
    {
      key: 'teamsAssigned',
      label: 'Teams Assigned',
      sortable: true,
      width: 200,
      render: (m) => (m.teamMates?.length ? (
        <div className="flex flex-wrap gap-1">
          {m.teamMates.map((tm) => (
            <span key={tm.team.id} className={TEAM_CHIP} style={{ background: '#e8eef7', color: '#213d69' }}>{tm.team.name}</span>
          ))}
        </div>
      ) : (
        <span className={TEAM_CHIP} style={{ background: '#f1f2f5', color: '#546077' }}>Unassigned</span>
      )),
    },
    {
      key: 'screenshot',
      label: (
        <span>
          Screenshot
          <span className="block text-ink-500" style={{ fontSize: 10, lineHeight: '10px', fontWeight: 400 }}>(10 mins period)</span>
        </span>
      ),
      sortable: true,
      width: 150,
      render: (m) => (
        <span className="inline-flex items-center gap-1.5 text-ink-700">
          <span className="tabular-nums">{`${m.screenshotsPerPeriod}x`}</span>
          {m.blurScreenshots ? <span className={TEAM_CHIP} style={{ background: '#eef5ea', color: '#458a44' }}>Blur</span> : null}
        </span>
      ),
    },
    {
      key: 'webcamAudit',
      label: 'Webcam Audit',
      sortable: true,
      width: 140,
      render: (m) => <span className="text-ink-700">{m.webcamAuditsPerPeriod}</span>,
    },
    {
      key: 'ptoPolicy',
      label: 'PTO Policy',
      sortable: true,
      width: 150,
      render: (m) => <span className="text-ink-700">{m.offTimePolicies?.length ? m.offTimePolicies[0].name : 'None'}</span>,
    },
    {
      key: 'location',
      label: 'Location',
      sortable: true,
      width: 140,
      render: (m) => <span className="text-ink-700">{m.user?.userLocation?.country || '—'}</span>,
    },
    {
      key: 'action',
      label: '',
      width: 72,
      align: 'center',
      render: (m) => (
        <RowActionMenu
          ariaLabel={`Actions for ${m.user?.fullName || 'member'}`}
          items={[
            { label: 'Edit Employee', onClick: () => { setSelectedMember(m); setShowEditModal(true); } },
            { label: 'View Activity', onClick: () => history.push({ pathname: '/members/activity/view', search: `?member=${m.id}`, state: { workspaceMember: m } }) },
          ]}
        />
      ),
    },
  ];

  const ghostBtn = 'inline-flex items-center justify-center gap-1.5 box-border h-10 px-4 border border-rule rounded-[8px] text-sm font-medium bg-white hover:bg-gray-50 transition';

  return (
    <>
      {showEditModal && (
        <EditModal
          member={selectedMember}
          closeModal={() => setShowEditModal(false)}
          onMemberSaved={() => {
            getMembers({
              page: currentPage,
              pageSize,
            });
          }}
          workspaceId={activeWorkspace.id}
          teams={teams}
          ptoPolicies={ptoPolicies}
        />
      )}

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 pt-4 pb-3">
        <div className="min-w-0">
          <h4 className="text-base font-semibold text-ink-900 mb-0.5">Team Audit &amp; Filtering</h4>
          <p className="text-sm text-ink-500">Manage employees&apos; team assignments, roles, and permissions.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={handleUnassigned}
            className={`${ghostBtn} ${onlyUnassigned ? 'text-ink-900' : 'text-[#CC4525]'}`}
            style={{ borderStyle: 'solid' }}
          >
            {onlyUnassigned ? 'All Members' : 'Unassigned Members'}
          </button>
          <button
            type="button"
            onClick={handleFilterClick}
            className="inline-flex items-center justify-center gap-2 box-border h-10 px-4 rounded-[8px] text-sm font-semibold text-white bg-brand-700 hover:bg-[#1a3156] transition"
          >
            <Filter size={16} />
            Filters
          </button>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleFilterClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{ '& .MuiMenu-list': { padding: 0 }, '& .MuiPaper-root': { borderRadius: '12px', border: '1px solid #e0e1e6', boxShadow: '0 10px 15px -3px rgba(14,30,64,0.1)', marginTop: '4px' } }}
          >
            <div className="p-4" style={{ width: 340, fontFamily: 'Inter, sans-serif' }}>
              <span className="block text-base font-semibold text-ink-900 mb-3">Filters</span>

              <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-1.5">Employee Name</span>
              <MultiSelect redesign options={filterMembers} value={selectedMembers} handleChange={setSelectedMembers} />

              <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-1.5 mt-4">Designation</span>
              <MultiSelect redesign options={[{ id: 0, name: 'All' }, ...filterDesignations]} value={selectedDesignations} handleChange={setSelectedDesignations} />

              <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-1.5 mt-4">Teams Assigned</span>
              <MultiSelect redesign options={[{ id: 0, name: 'All' }, ...filterTeams]} value={selectedTeams} handleChange={setSelectedTeams} />

              <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-1.5 mt-4">Location</span>
              <MultiSelect redesign options={[{ id: 0, name: 'All' }, ...locations]} value={selectedLocations} handleChange={setSelectedLocations} />

              <div className="flex justify-end items-center gap-3 mt-5">
                <button type="button" onClick={handleReset} className="text-sm font-medium text-ink-900 hover:text-ink-700 bg-transparent border-0 cursor-pointer p-0">Reset</button>
                <button type="button" onClick={() => { handleSearch(); handleFilterClose(); }} className="inline-flex items-center justify-center box-border h-9 px-5 rounded-[8px] text-sm font-semibold text-white bg-[#53A551] hover:bg-[#458a44] transition">Search</button>
              </div>
            </div>
          </Menu>
        </div>
      </div>

      <DataTable
        bare
        columns={columns}
        rows={members}
        getRowKey={(m) => m.id}
        sortKey={sortKey}
        sortDir={sortOrder}
        onSort={toggleSort}
        loading={isLoading}
        emptyText="No members found."
        page={currentPage}
        pageSize={pageSize}
        total={totalCounts}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        minWidth={1100}
      />
    </>
  );
}

export default TeamManagement;
